import { createAsyncThunk } from "@reduxjs/toolkit";

import { DEV_MODE } from "../../../Constant/app/process";
import {
    getConnectionDetail,
    getDebugModeResults,
    setDebugMode,
} from "../../../helpers/leads/debug";
import {
    buildDevMockConnectionDetail,
    buildDevMockDebugResults,
} from "../../../pages/LeadDebugPage/LeadDebugPage.mock";
import type {
    ConnectionDetailData,
    DebugLog,
} from "../../../pages/LeadDebugPage/LeadDebugPage.types";
import {
    defaultPagination,
    GetDebugModeResultsPayload,
    SetDebugModePayload,
} from "./leadDebug.types";

export const getConnectionDetailAsync = createAsyncThunk(
    "leadDebug/getConnectionDetail",
    async (connectionId: string) => {
        const res = await getConnectionDetail(connectionId);
        if (!res.status || !res.data) {
            if (DEV_MODE) return buildDevMockConnectionDetail(connectionId);
            throw new Error(res.message ?? "Failed to load connection");
        }
        return res.data as ConnectionDetailData;
    },
);

export const getDebugModeResultsAsync = createAsyncThunk(
    "leadDebug/getDebugModeResults",
    async ({ connectionId, params }: GetDebugModeResultsPayload) => {
        const res = await getDebugModeResults(connectionId, params);
        const logs = (res.data?.debug_logs ?? []) as DebugLog[];

        // Fall back to the fixtures only when the backend gave us nothing, so
        // a real debug session is never masked by mock rows.
        if (DEV_MODE && !logs.length) {
            return buildDevMockDebugResults(connectionId, params);
        }

        if (!res.status || !res.data)
            throw new Error(res.message ?? "Failed to load debug logs");
        return {
            debug_logs: logs,
            pagination: res.data.pagination ?? defaultPagination,
        };
    },
);

export const setDebugModeAsync = createAsyncThunk(
    "leadDebug/setDebugMode",
    async ({ connectionId, debug_mode }: SetDebugModePayload) => {
        const res = await setDebugMode(connectionId, debug_mode);
        if (!res.status) {
            // Keep the toggle usable against a backend that cannot serve it.
            if (DEV_MODE) return { connectionId, debug_mode };
            throw new Error(res.message ?? "Failed to set debug mode");
        }
        return { connectionId, debug_mode };
    },
);
